import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTokenForRequest } from '../../utils/apiAuth';
import './ScaffoldApproval.css';

/**
 * Helper function to encode token for API calls
 * @param {string} token - The session token
 * @returns {string} - Encoded token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * Helper function to format .NET date string to JavaScript Date
 * @param {string} dotnetDate - .NET date string like "/Date(1234567890)/"
 * @returns {string} - Formatted date string
 */
const formatDotNetDate = (dotnetDate) => {
  if (!dotnetDate) return 'NA';
  
  try {
    const match = dotnetDate.match(/\/Date\((\d+)\)\//);
    if (match) {
      const timestamp = parseInt(match[1]);
      return new Date(timestamp).toLocaleString();
    }
    return 'NA';
  } catch (error) {
    return 'NA';
  }
};

/**
 * ScaffoldApproval Component
 * Displays list of scaffolds pending approval with approve/reject functionality
 */
const ScaffoldApproval = ({ token }) => {
  const [scaffolds, setScaffolds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadScaffolds();
  }, []);

  /**
   * Load scaffolds pending approval
   */
  const loadScaffolds = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios({
        url: `./webservices/Scaffold.svc/pendingScaffolds?token=${encodeToken(getTokenForRequest())}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data && response.data.Scaffolds) {
        setScaffolds(response.data.Scaffolds);
      } else {
        setScaffolds([]);
      }
    } catch (error) {
      console.error('Failed to load scaffolds:', error);
      setError('Failed to load scaffolds. Please try again.');
      setScaffolds([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get status display text
   */
  const getStatusText = (status) => {
    if (status === 'A') return 'Approved';
    if (status === 'R') return 'Rejected';
    return 'Pending Approve';
  };

  /**
   * Get status CSS class
   */
  const getStatusClass = (status) => {
    if (status === 'A') return 'text-success';
    if (status === 'R') return 'text-danger';
    return 'text-warning';
  };

  /**
   * Approve scaffold
   */
  const approveScaffold = async (scaffold) => {
    if (!window.confirm('Are you sure you want to approve this scaffold?')) {
      return;
    }

    setProcessingId(scaffold.ID);

    try {
      const response = await axios({
        url: './webservices/Scaffold.svc/UpdateScaffold',
        method: 'POST',
        data: {
          token: encodeToken(getTokenForRequest()),
          scaffoldId: scaffold.ID,
          description: scaffold.Description || '',
          filePath: scaffold.FileID || '',
          scaffoldType: scaffold.TypeDesc || '',
          topicID: scaffold.TopicID || null,
          status: 'A',
          screenshot: scaffold.screenshot || null,
          videoType: scaffold.videoType || null
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        alert('Scaffold approved successfully.');
        // Reload the list
        await loadScaffolds();
      }
    } catch (error) {
      console.error('Failed to approve scaffold:', error);
      alert("Can't approve scaffold at this moment. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Reject scaffold
   */
  const rejectScaffold = async (scaffold) => {
    if (!window.confirm('Are you sure you want to reject this scaffold?')) {
      return;
    }

    setProcessingId(scaffold.ID);

    try {
      const response = await axios({
        url: './webservices/Scaffold.svc/UpdateScaffold',
        method: 'POST',
        data: {
          token: encodeToken(getTokenForRequest()),
          scaffoldId: scaffold.ID,
          description: scaffold.Description || '',
          filePath: scaffold.FileID || '',
          scaffoldType: scaffold.TypeDesc || '',
          topicID: scaffold.TopicID || null,
          status: 'R'
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        alert('Scaffold rejected successfully.');
        // Reload the list
        await loadScaffolds();
      }
    } catch (error) {
      console.error('Failed to reject scaffold:', error);
      alert("Can't reject scaffold at this moment. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Preview scaffold
   */
  const previewScaffold = (scaffold) => {
    // Use existing previewFile function if available
    if (typeof window.previewFile === 'function') {
      window.previewFile(scaffold.FileID, scaffold.TypeDesc || 'Video', scaffold.ID);
    } else {
      // Fallback: open in new window
      if (scaffold.FileID) {
        window.open(scaffold.FileID, '_blank');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="scaffold-approval-container">
        <div className="header">Scaffolds Pending Approve</div>
        <div className="text-center" style={{ padding: '40px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scaffold-approval-container">
        <div className="header">Scaffolds Pending Approve</div>
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-primary" onClick={loadScaffolds}>Retry</button>
      </div>
    );
  }

  return (
    <div className="scaffold-approval-container">
      <div className="header">Scaffolds Pending Approve</div>
      
      {scaffolds.length === 0 ? (
        <div className="alert alert-info">No scaffold pending approval.</div>
      ) : (
        <div className="table-responsive top-margin">
          <table id="scaffolds" className="table table-striped">
            <thead>
              <tr>
                <th>Status</th>
                <th>Description</th>
                <th>Filename</th>
                <th>Type</th>
                <th>Topic</th>
                <th>Updated On</th>
                <th>Preview</th>
                <th>Approve</th>
                <th>Reject</th>
                {scaffolds.some(s => s.CamtasiaID) && <th>Camtasia</th>}
              </tr>
            </thead>
            <tbody>
              {scaffolds.map((scaffold) => (
                <tr 
                  key={scaffold.ID} 
                  style={scaffold.Status === 'R' ? { color: 'red' } : {}}
                >
                  <td>
                    <span className={getStatusClass(scaffold.Status)}>
                      {getStatusText(scaffold.Status)}
                    </span>
                  </td>
                  <td>
                    <div className="scaffold-desc">
                      {scaffold.Description || '-'}
                    </div>
                  </td>
                  <td>{scaffold.Filename || '-'}</td>
                  <td>{scaffold.TypeDesc || '-'}</td>
                  <td>{scaffold.TopicName || '-'}</td>
                  <td>{formatDotNetDate(scaffold.CreatedDate)}</td>
                  <td>
                    <button
                      className="btn btn-default"
                      onClick={() => previewScaffold(scaffold)}
                      title="Preview"
                    >
                      VET
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-success"
                      onClick={() => approveScaffold(scaffold)}
                      disabled={processingId === scaffold.ID}
                      title="Approve"
                    >
                      {processingId === scaffold.ID ? 'Processing...' : 'Approve'}
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-danger"
                      onClick={() => rejectScaffold(scaffold)}
                      disabled={processingId === scaffold.ID}
                      title="Reject"
                    >
                      {processingId === scaffold.ID ? 'Processing...' : 'Reject'}
                    </button>
                  </td>
                  {scaffolds.some(s => s.CamtasiaID) && (
                    <td>
                      {scaffold.CamtasiaID ? (
                        <button
                          className="btn"
                          onClick={() => {
                            const camtasiaURL = window.camtasiaURL || './CamtasiaPlayer.aspx';
                            window.open(`${camtasiaURL}?camtasiaID=${scaffold.CamtasiaID}`, '_blank');
                          }}
                          title="Preview Camtasia"
                        >
                          <i className="glyphicon glyphicon-eye-open"></i>
                        </button>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScaffoldApproval;

